import { z } from "zod";
//#region src/app-pod-log.ts
/**
* App-scoped pod-log wire types —
* `GET /v1/admin/apps/:appId/dps/:dpId/pods/:podName/logs`
* (site-admin-api observability module; `design/02` §2.2 **E9**, §5.4).
*
* The one question Loki structurally cannot answer: **what did the container
* print before it died?** Loki may never have received those lines — a crash
* before a flush loses them — and it has no notion of a *previous* container
* instance at all. This reads the kubelet directly, one shot, bounded.
*
* ## ⚠️ A SECOND CONSUMER, AND THE SCHEMAS ARE DELIBERATELY SHARED
*
* `GET /v1/admin/control-plane/pods/:podName/logs` (#1251) binds
* {@link appPodLogQuerySchema} and returns {@link AppPodLogResponse} unchanged.
* The question, the bounds and both truncation rulings are identical, so a
* `cp`-named copy would be a fork with a different name. ⛔ Do not "specialise"
* either schema for one of the two callers — narrow at the controller instead.
*
* ## Two capabilities, two endpoints, no conflation (§5.4)
*
* **NOT a stream, and `follow` is deliberately absent.** Live tail is E8, on a
* different transport, answering a different question ("what is it printing
* *now*"). Offering `follow` here would collapse the two and re-open the
* direct-k8s streaming pattern R3 closed.
*
* ## ⛔ Why this endpoint was cut from R4 and ships now
*
* R2-05 condition 1 forbade any user-facing caller of the `pods/log` transport
* while **#736** was open, and E9 is the only user-facing caller there is
* (**R4-10**). Its spec was retained verbatim rather than dropped, on Dave's
* written authorization, and this is that spec.
*
* ⚠️ **#736 ships in PERMISSIVE/AUDIT mode, not enforcing** — so the L7
* service-identity policy is **not** a control anything here may lean on.
* CP-side narrowing is the ONLY tenancy control on this path, with no defence
* in depth behind it (**R4-24**, recorded as a knowing, time-boxed exception).
* That is why the two scope checks in `app-pod-log.service.ts` are load-bearing
* rather than belt-and-braces, and why both are ordered before the outbound
* call.
*
* ## Absence is typed here too
*
* `empty` ≠ `errorstate` ≠ `notcollected` is the canon this release train
* exists to establish, and a log endpoint is where it is easiest to lose:
*   - `lines: []` means **the container printed nothing**. It is never a
*     stand-in for a failure. Every failure on this path is an HTTP error
*     carrying a message.
*   - A pod we could not reach is a **502**, never an empty page.
*   - A log too large to return is a **413** that names its own remedy — see
*     {@link appPodLogResponseSchema}'s `truncated` note for why it is an
*     error and not a flag.
*/
/**
* Lines returned when the caller does not ask (`design/02` §2.2 E9:
* `int [1,2000], default 500`).
*/
const DEFAULT_TAIL_LINES = 500;
/** Hard ceiling on `tailLines`. */
const MAX_TAIL_LINES = 2e3;
/**
* Filterable query params. `.strict()` (#466) rejects unknown keys at every
* depth, which is what keeps `follow`, `namespace`, `labelSelector`, `orgId`
* and friends off this surface: they are not omissions to be filled in, they
* are rejections. `namespace` in particular is server-derived from `:dpId`
* inside provisioning-svc and must never be client-supplied.
*/
const appPodLogQuerySchema = z.object({
	/**
	* Which container in the pod. Omitted ⇒ the **data plane** resolves it:
	* exactly one container is used as-is, more than one is a 400. The DP never
	* guesses, and neither does the CP.
	*
	* ⛔ The 400 does NOT name the available containers, and this line used to
	* say it did. The names arrive inside the DP's error body — the same string
	* that carries `.svc` hostnames and other tenants' namespace names — so
	* forwarding them to an app-owner audience would breach the no-upstream-body
	* rule the whole proxy chain is built on. The message instead points at
	* `GET /v1/admin/apps/:appId/dps/:dpId/pods`, which is gated on the same
	* grant and returns `containers[].name`, so the caller gets the fact with
	* nothing upstream crossing the boundary.
	*
	* ⇒ If you are tempted to "fix" this by forwarding the DP's text: that is
	* the defect, not the gap. A test asserts the names are absent and the
	* pointer is present.
	*
	* DNS-1123 *label* (no dots, ≤ 63) — the k8s container-name grammar. The
	* length bound is checked before the pattern, per the repo's ReDoS rule.
	*/
	container: z.string().min(1).max(63).regex(/^[a-z0-9]([-a-z0-9]{0,61}[a-z0-9])?$/).optional(),
	/**
	* Read the **terminated previous instance** rather than the current one —
	* the `--previous` capability, and the entire reason this endpoint exists
	* alongside E7/E8 (§5.4).
	*
	* ⛔ **A two-value STRING enum, NOT `z.boolean()` and NOT
	* `z.coerce.boolean()`.** This arrives as a query string, and
	* `z.coerce.boolean()` maps the string `'false'` to `true` — every
	* non-empty string is truthy — so `?previous=false` would silently turn
	* the flag **on** and serve the wrong container instance. Identical
	* reasoning, and identical shape, to `appPodsQuerySchema.includeTerminated`
	* (E3); widened to a real boolean once, at the controller boundary.
	*
	* Absent ⇒ `false`.
	*/
	previous: z.enum(["true", "false"]).optional(),
	/**
	* Lines from the END of the log, `[1, MAX_TAIL_LINES]`, default
	* {@link DEFAULT_TAIL_LINES}. `z.coerce` because it arrives as a query
	* string.
	*
	* ⚠️ Counter-intuitive but load-bearing: when a log is too large to
	* return, the remedy is a **SMALLER** `tailLines`, not a larger one. The
	* data plane caps the bytes it will read and returns the *oldest* bytes of
	* the requested tail window, so a narrower window sits closer to now —
	* which is where the crash is. The 413 message says so.
	*/
	tailLines: z.coerce.number().int().min(1).max(MAX_TAIL_LINES).optional()
}).strict();
/**
* One pod's container log.
*
* The field set is `design/02` §2.2 E9 verbatim. Note it deliberately does NOT
* echo `appId`/`dpId` the way E3 and E4 do — a pod name is already globally
* unique within a namespace and the caller named it, so there is nothing to
* disambiguate on a fan-out.
*/
const appPodLogResponseSchema = z.object({
	/** Echoed verbatim, so a client that fanned out can attribute the answer. */
	podName: z.string().min(1),
	/**
	* The **RESOLVED** container — never absent, even when the request omitted
	* it. A caller that let the DP choose still learns what it chose.
	*/
	container: z.string().min(1),
	/**
	* The **EFFECTIVE** value, so `false` is affirmative rather than merely
	* "unset". A caller reading a crashed container's log needs to know which
	* instance it got.
	*/
	previous: z.boolean(),
	/**
	* Log lines, **oldest first**, without trailing newlines.
	*
	* ⛔ `[]` means the container printed nothing. It is NEVER a rendering of
	* a failure — that is the `empty`-as-`errorstate` collapse this release
	* exists to remove, and a log viewer is the single worst place to make it,
	* because "no output" is itself a plausible diagnosis and a user will
	* believe it.
	*/
	lines: z.array(z.string()),
	/**
	* TRUE ⇒ output was cut at `tailLines` and **older** lines exist.
	*
	* Determined EXACTLY by the data plane, not by `lines.length === tailLines`:
	* it asks the kubelet for `tailLines + 1` and truncation is
	* `received > tailLines`. An exactly-full page is the commonest honest
	* answer and flagging it would train callers to ignore the field.
	*
	* ## ⛔ This flag has exactly ONE cause, and the other one is a 413
	*
	* There is a second way a log can be cut — the data plane's **byte** bound
	* (#878) — and it deliberately does **not** set this flag. It is an HTTP
	* **413** instead, and the reason is worth stating because "surface it as
	* a truncation flag" is the obvious-looking wrong move:
	*
	* The byte cap makes the kubelet **stop sending**, and the kubelet seeks to
	* the start of the tail window and reads **forward**. So a byte-capped body
	* is *the beginning of the requested window* — meaning **the crash the user
	* opened the log to read is precisely what got dropped.** A `truncated:
	* true` on that response would hand a debugging tool the wrong end of the
	* log with a sticker on it, and a user who trusts the sticker still cannot
	* see the crash. The 413 is more honest: no prefix to mistake for a log,
	* and a message naming the remedy that actually works.
	*
	* ⇒ **`truncated: true` always means "older lines exist, ask for more".
	* A 413 always means "ask for fewer".** Two different cuts, two different
	* remedies, and they point in opposite directions — which is exactly why
	* they must not share one boolean.
	*
	* ⚠️ `truncated: false` is NOT a proof of completeness. The k8s API
	* documents its byte limit as possibly returning "slightly more or
	* slightly less than the specified limit", so a server that truncated
	* while returning less than the cap cannot be detected. Do not build a
	* "this is the whole log" claim on this field.
	*/
	truncated: z.boolean()
}).strict();
//#endregion
export { DEFAULT_TAIL_LINES, MAX_TAIL_LINES, appPodLogQuerySchema, appPodLogResponseSchema };

//# sourceMappingURL=app-pod-log.js.map